import { Field, useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import React from 'react'

import { StockTooltip } from '@components/common/StockTooltip'
import { TableCell } from '@components/common/TableCell'
import { WeightInput } from '@components/common/WeightInput'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import {
  CurrencyInput,
  Input,
  List,
  PriceString,
  Table,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import type { Offer } from '@fourthwall/services/dist/models/offer'

import type { ProductFormState } from '../../../../SingleProduct.types'
import type { VariantItem } from '../../types'
import type { ProductVariantModalFormValues } from '../ProductVariantModal'
import { ProductVariantModal } from '../ProductVariantModal'
import { ProductVariantsListItem } from '../ProductVariantsListItem'
import { VariantImageCell } from '../VariantImageCell'
import * as S from './styled'

type ProductVariantsDataProps = {
  data: VariantItem[]
  showWeight: boolean
  showPricing: boolean
  showExternalStock?: boolean
  disabled?: boolean
  stockDisabled?: boolean
  onStockTooltipChange: (
    variantId: string,
  ) => (oldValue: number | undefined, newValue: number | undefined) => void
}

export const ProductVariantsData = ({
  data,
  showWeight,
  showPricing,
  showExternalStock,
  disabled,
  stockDisabled,
  onStockTooltipChange,
}: ProductVariantsDataProps) => {
  const media = useMediaQueryContext()
  const { open } = useModalContext()
  const { hasPermission } = useDashboardPermissions()
  const { values, setValues } = useFormikContext<ProductFormState>()

  const findVariantIndexById = (variantId: string) =>
    values.variants.findIndex(({ id }) => id === variantId)

  const handleProductVariantModalSubmit =
    (index: number) => (values: ProductVariantModalFormValues) => {
      const updatedVariant = {
        ...data[index],
        ...values,
      }

      if (!isEqual(data[index], updatedVariant)) {
        setValues((prevState) => ({
          ...prevState,
          variants: prevState.variants.map((variant, i) =>
            i === index ? updatedVariant : variant,
          ),
        }))
      }
    }

  const headers = [
    {
      id: 'name',
      label: 'Variant',
      minWidth: 250,
      render: ({
        attributesDescription,
        images,
        previewImage,
        isFirstOfGroup,
        isLastOfGroup,
      }: VariantItem) => (
        <div style={{ display: 'flex', alignItems: 'center', width: 'auto' }}>
          <VariantImageCell
            previewImage={previewImage.url}
            showPreviewImage={!previewImage.hidden}
            imagesCount={images.length}
            isFirstOfGroup={isFirstOfGroup}
            isLastOfGroup={isLastOfGroup}
          />
          <div>{attributesDescription}</div>
        </div>
      ),
    },
    {
      id: 'price.value',
      label: 'Selling Price',
      hidden: !showPricing,
      minWidth: 150,
      render: (variant: Offer.Variant) => (
        <TableCell
          mainContent={
            <S.PriceWrapper>
              <Field
                name={`variants[${findVariantIndexById(variant.id)}].price.value`}
                component={CurrencyInput}
                size="small"
                displayError={false}
                disabled={disabled}
              />
            </S.PriceWrapper>
          }
        />
      ),
    },
    {
      id: 'compareAtPrice',
      label: 'Compare-at price',
      hidden: !showPricing || !values.isCompareAtPriceEnabled,
      minWidth: 150,
      render: (variant: Offer.Variant) => (
        <TableCell
          mainContent={
            <S.PriceWrapper>
              <Field
                name={`variants[${findVariantIndexById(variant.id)}].compareAtPrice.value`}
                component={CurrencyInput}
                strikethrough
                size="small"
                displayError={false}
                disabled={disabled}
              />
            </S.PriceWrapper>
          }
        />
      ),
    },
    {
      id: 'declaredCost',
      label: 'Product cost',
      hidden: !showPricing || !values.isCreatorDeclaredCostEnabled,
      width: 0,
      minWidth: 150,
      render: (variant: Offer.Variant) => (
        <TableCell
          mainContent={
            <S.PriceWrapper>
              <Field
                name={`variants[${findVariantIndexById(variant.id)}].creatorDeclaredCost.value`}
                component={CurrencyInput}
                size="small"
                displayError={false}
                disabled={disabled}
              />
            </S.PriceWrapper>
          }
        />
      ),
    },
    {
      id: 'quantity',
      label: 'In Stock',
      minWidth: 95,
      render: ({ id, quantity }: VariantItem) => (
        <TableCell
          mainContent={
            <StockTooltip
              quantity={quantity}
              disabled={stockDisabled}
              onChange={onStockTooltipChange(id)}
            />
          }
        />
      ),
    },
    {
      id: 'sold',
      label: 'Sold',
      hidden: !hasPermission('contributions.profit'),
      minWidth: 60,
      render: ({ sold }: VariantItem) => <TableCell mainContent={sold} />,
    },
    {
      id: 'external_stock',
      label: 'In fulfillment center',
      hidden: !showExternalStock,
      minWidth: 60,
      render: ({ externalStock }: VariantItem) => <TableCell mainContent={externalStock} />,
    },
    {
      id: 'weight.value',
      label: 'Weight',
      hidden: !showWeight,
      minWidth: 155,
      render: (variant: VariantItem) => (
        <TableCell
          mainContent={
            <S.WeightContainer>
              <WeightInput
                weightName={`variants[${findVariantIndexById(variant.id)}].weight.value`}
                unitName={`variants[${findVariantIndexById(variant.id)}].weight.unit`}
                size="small"
                weightWidth={80}
                unitWidth={76}
                disabled={disabled}
              />
            </S.WeightContainer>
          }
        />
      ),
    },
    {
      id: 'sku',
      label: 'SKU or UPC code',
      hidden: !values.visualHints.customSkus,
      minWidth: 175,
      render: (variant: VariantItem) => (
        <TableCell
          mainContent={
            <Field
              name={`variants[${findVariantIndexById(variant.id)}].sku`}
              displayError={false}
              component={Input}
              size="small"
              disabled={disabled}
            />
          }
        />
      ),
    },
    {
      id: 'profit',
      hidden: !showPricing || !hasPermission('contributions.profit'),
      label: 'Profit Per Sale',
      minWidth: 100,
      render: (variant: VariantItem) => {
        const cost = variant.cost?.value

        return cost ? <PriceString value={variant.price.value - cost} /> : <>&ndash;</>
      },
    },
  ]

  if (!media.tablet) {
    return (
      <List
        data={data}
        renderItem={(productVariant: VariantItem) => (
          <ProductVariantsListItem
            productVariant={productVariant}
            previewImage={productVariant.previewImage.url}
            showPreviewImage={!productVariant.previewImage.hidden}
            showPricing={showPricing}
            showWeight={showWeight}
            showSKU={values.visualHints.customSkus}
          />
        )}
        onClick={(productVariant: VariantItem, index: number) => {
          if (disabled) return

          open(ProductVariantModal, {
            productVariant,
            isPriceEditable: showPricing,
            isCompareAtPriceEditable: showPricing && values.isCompareAtPriceEnabled,
            isCreatorDeclaredCostEditable: showPricing && values.isCreatorDeclaredCostEnabled,
            isWeightEditable: showWeight,
            isSKUEditable: values.visualHints.customSkus,
            onStockTooltipChange: onStockTooltipChange(productVariant.id),
            onSubmit: handleProductVariantModalSubmit(index),
          })
        }}
      />
    )
  }

  return <Table data={data} headers={headers.filter((header) => !header.hidden)} minWidth={0} />
}
